import React, { useState } from "react";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

// Subject title + switcher, shared by the v2 SBG class layout and the legacy (v1)
// class layout's page header. Renders the switch icon, the active subject name,
// and a dropdown menu of subjects to jump to. It owns only the menu affordance -
// each consumer supplies `items` carrying their own onSelect callbacks, so the
// SBG layout can route to a class path while the legacy layout routes to a
// subject path. No navigation, no data fetching lives here.
//
// When there is nothing to switch to the dropdown affordance is omitted and
// only the name shows.
export default function SubjectSwitcherTitle({
  subjectName,
  items = [],
  fontSize = 24,
}) {
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);
  const close = () => setAnchor(null);

  const handlePick = (onSelect) => () => {
    close();
    onSelect?.();
  };

  const hasMenu = items.length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minWidth: 0,
        fontSize,
      }}
    >
      {/* The dropdown-arrow trigger leads the title (left of the subject name);
          there is no longer an icon before the name. */}
      {hasMenu && (
        <IconButton
          size="small"
          aria-label="Switch subject"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ flexShrink: 0 }}
        >
          <KeyboardArrowDownIcon />
        </IconButton>
      )}
      <Box
        component="span"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
      >
        {subjectName}
      </Box>
      {hasMenu && (
        <Menu
          anchorEl={anchor}
          open={open}
          onClose={close}
          slotProps={{ list: { "aria-label": "Subjects" } }}
        >
          {items.map((item) => (
            <MenuItem
              key={item.key}
              selected={item.selected}
              disabled={item.disabled}
              onClick={handlePick(item.onSelect)}
              sx={{ justifyContent: "space-between", gap: 2 }}
            >
              {item.label}
              {/* Small gray marker after the name (e.g. "SBG" on rows that
                  open the Standards-based Grading class view). */}
              {item.tag && (
                <Typography
                  component="span"
                  sx={{ color: "text.secondary", fontSize: 12 }}
                >
                  {item.tag}
                </Typography>
              )}
            </MenuItem>
          ))}
        </Menu>
      )}
    </Box>
  );
}
